import { useLanguage } from "@/components/language-provider";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Phone } from "lucide-react";
import { BrandLogo } from "@/components/brand-logo";
import { API_BASE_URL } from "@/lib/config";

export default function VerifyPhone() {
  const { t } = useLanguage();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const authHeaders = (): Record<string, string> => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLocation("/login");
      return;
    }
    // Prefill the phone from the account; skip the page if already verified.
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/phone-status`, { headers: authHeaders() });
        if (res.ok) {
          const d = await res.json();
          if (d.verified) {
            setLocation("/dashboard");
            return;
          }
          if (d.phoneNumber) setPhone(d.phoneNumber);
        }
      } catch { /* ignore */ }
    })();
  }, [setLocation]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendCode = async () => {
    if (!phone.trim()) {
      toast({ title: t("ui.phone_required_249"), description: t("ui.enter_your_whatsapp_number_with_country_code_e_g"), variant: "destructive" });
      return;
    }
    setSendingCode(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/send-phone-verification`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ phoneNumber: phone.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not send the code.");

      toast({ title: t("ui.code_sent"), description: t("ui.check_your_whatsapp_for_the_6_digit_code") });
      setCodeSent(true);
      setResendCooldown(60);
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || code.trim().length !== 6) {
      toast({ title: t("ui.invalid_code"), description: t("ui.enter_the_6_digit_verification_code"), variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/verify-phone`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Invalid or expired code.");

      toast({ title: t("ui.phone_verified"), description: t("ui.your_phone_number_has_been_verified_successfully") });
      setLocation("/dashboard");
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <BrandLogo />
          </div>
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Phone className="w-6 h-6 text-primary" />
          </div>
          <CardTitle>{t("ui.verify_your_phone")}</CardTitle>
          <CardDescription>
            {t("ui.we_ll_send_a_6_digit_code_to_your_whatsapp_to_co")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">{t("ui.whatsapp_number")}</Label>
              <div className="flex gap-2">
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+55 31 99999-9999"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={sendingCode}
                  data-testid="input-phone"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendCode}
                  disabled={sendingCode || resendCooldown > 0}
                  data-testid="button-send-code"
                >
                  {sendingCode ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : resendCooldown > 0 ? (
                    `${resendCooldown}s`
                  ) : codeSent ? (
                    "Resend"
                  ) : (
                    "Send code"
                  )}
                </Button>
              </div>
            </div>

            {codeSent && (
              <div className="space-y-2">
                <Label htmlFor="code">{t("ui.verification_code")}</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="text-center text-lg tracking-[0.5em]"
                  data-testid="input-code"
                />
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading || !codeSent} data-testid="button-verify-phone">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
